import { useState } from "react";
import { IScheduleOverride } from "../models/IAvailability";
import { cancelOverride } from "../services/availabilityService";
import { apiErrorMessage, describeWindow, parseUtc } from "../utils/workingHoursUtils";
import AddOverrideModal from "./AddOverrideModal";

interface Props {
  restaurantId: string;
  overrides: IScheduleOverride[];
  loading: boolean;
  error: string;
  onChanged: () => void;
}

export default function OverridesPanel({ restaurantId, overrides, loading, error, onChanged }: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");

  const byStart = (a: IScheduleOverride, b: IScheduleOverride) =>
    (parseUtc(a.startsAtUtc)?.getTime() ?? 0) - (parseUtc(b.startsAtUtc)?.getTime() ?? 0);
  const active = overrides.filter((o) => o.isActive).sort(byStart);
  const upcoming = overrides.filter((o) => !o.isActive).sort(byStart);

  const cancel = async (o: IScheduleOverride) => {
    setBusyId(o.id);
    setActionError("");
    try {
      await cancelOverride(o.id);
      setConfirmId(null);
      onChanged();
    } catch (err) {
      setActionError(apiErrorMessage(err, "Kayıt kaldırılamadı."));
    } finally {
      setBusyId(null);
    }
  };

  const renderItem = (o: IScheduleOverride) => {
    const w = describeWindow(o);
    const color = o.isClosed ? "#dc2626" : "#059669";
    return (
      <div key={o.id} className="wh-card" style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", borderLeft: `4px solid ${color}` }}>
        <span style={{
          width: 38, height: 38, borderRadius: 11, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: o.isClosed ? "#fef2f2" : "#ecfdf5", color, fontSize: "1rem",
        }}>
          <i className={`bi ${o.isClosed ? "bi-calendar-x-fill" : "bi-calendar-check-fill"}`}></i>
        </span>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, fontSize: ".88rem", color: "#111827" }}>{w.title}</span>
            <span style={{ fontSize: ".66rem", fontWeight: 800, padding: "2px 8px", borderRadius: 20, background: o.isClosed ? "#fee2e2" : "#dcfce7", color }}>
              {o.isClosed ? "Kapalı" : "Özel açılış"}
            </span>
            {o.isActive && (
              <span style={{ fontSize: ".66rem", fontWeight: 800, padding: "2px 8px", borderRadius: 20, background: "#eef2ff", color: "#4f46e5" }}>
                <i className="bi bi-broadcast me-1"></i>Şu an geçerli
              </span>
            )}
          </div>
          <div style={{ fontSize: ".76rem", color: "#6b7280", marginTop: 2 }}>
            {w.sub}{o.reason ? <> · <i className="bi bi-chat-left-quote me-1"></i>{o.reason}</> : null}
          </div>
        </div>
        {confirmId === o.id ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: ".76rem", color: "#6b7280" }}>Kaldırılsın mı?</span>
            <button className="wh-btn wh-btn--danger" style={{ padding: "6px 12px" }} onClick={() => cancel(o)} disabled={busyId === o.id}>
              {busyId === o.id ? <span className="spinner-border spinner-border-sm"></span> : "Evet"}
            </button>
            <button className="wh-btn" style={{ padding: "6px 12px" }} onClick={() => setConfirmId(null)} disabled={busyId === o.id}>Hayır</button>
          </div>
        ) : (
          <button className="wh-btn wh-btn--ghost-danger" style={{ padding: "6px 12px" }} onClick={() => setConfirmId(o.id)}>
            <i className="bi bi-x-circle"></i>{o.isActive ? "Sonlandır" : "İptal et"}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <p style={{ color: "#6b7280", fontSize: ".84rem", margin: 0, maxWidth: 560 }}>
          Haftalık programdan önceden planlanmış sapmalar: tatil kapanışları ya da normalde kapalı olunan bir günde özel açılış.
          Süreleri dolunca kendiliğinden kalkar.
        </p>
        <button className="wh-btn wh-btn--primary" onClick={() => setShowAdd(true)}>
          <i className="bi bi-plus-lg"></i>Yeni ekle
        </button>
      </div>

      {actionError && <div className="wh-note wh-note--error" style={{ marginBottom: 12 }}><i className="bi bi-exclamation-circle-fill"></i><span>{actionError}</span></div>}

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}><div className="spinner-border text-warning" role="status" /></div>
      ) : error ? (
        <div className="wh-note wh-note--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
      ) : overrides.length === 0 ? (
        <div style={{ textAlign: "center", padding: "44px 16px", color: "#9ca3af" }}>
          <i className="bi bi-calendar2-week" style={{ fontSize: "2.2rem", display: "block", marginBottom: 8, opacity: 0.5 }}></i>
          <div style={{ fontSize: ".9rem", fontWeight: 700, color: "#6b7280" }}>Planlanmış özel gün yok</div>
          <div style={{ fontSize: ".8rem", marginTop: 4 }}>Resmî tatil, bakım ya da özel açılışları buradan önceden girebilirsiniz.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {active.length > 0 && (
            <div>
              <div style={{ fontSize: ".7rem", fontWeight: 800, color: "#4f46e5", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Şu an geçerli</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{active.map(renderItem)}</div>
            </div>
          )}
          {upcoming.length > 0 && (
            <div>
              <div style={{ fontSize: ".7rem", fontWeight: 800, color: "#b45309", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Yaklaşan</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{upcoming.map(renderItem)}</div>
            </div>
          )}
        </div>
      )}

      {showAdd && <AddOverrideModal restaurantId={restaurantId} onClose={() => setShowAdd(false)} onSaved={onChanged} />}
    </>
  );
}
